import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { DISTRIBUTION_ERRORS } from 'shared/constants/GenericMessages';
import { formatCurrency, cloneObject } from 'shared/utils';
import { SortableTableHeader, SortableTableProvider, Table } from 'shared/table';
import { FormValue, FormWithValidation } from 'shared/form';
import { Cuenta } from 'core/cuenta/models/Cuenta';
import { Button, SearchInput } from '@orbita-ui/core';
import { TableSortFilterEvent } from 'shared/table/contexts/SortableTableContext';
import { highlightText } from 'shared/utils/highlightText';
import { TableRow } from 'shared/table/components/Table/Table';
import {
  ButtonPlaceholder,
  InputPlaceholder,
  TextPlaceholder,
} from 'shared/placeholders';
import { nanoid } from 'nanoid';
import { Card } from 'shared/components';

export const FundsDistributionList = ({
  saldoDisponible,
  destinos,
  isLoading,
  onSubmit,
  onClear,
}: {
  saldoDisponible: number;
  destinos: Cuenta[];
  isLoading: boolean;
  onSubmit?: () => void;
  onClear: () => void;
}) => {
  const [cuentas, setCuentas] = useState<Cuenta[]>(cloneObject(destinos));
  const [searchInput, setSearchInput] = useState<string>('');
  const sortRef = useRef(null);

  useEffect(() => {
    if (!isLoading) {
      setCuentas(cloneObject(destinos));
      setSearchInput('');
    }
  }, [isLoading, destinos]);

  const clearHandler = () => {
    searchEventHandler('');
    sortRef.current.reset();
    onClear();
  };

  const filterEventHandler = useCallback((e: TableSortFilterEvent) => {
    if (e) {
      const { columnName, sortOrder } = e;
      setCuentas((prev) => {
        const cuentasCopy = [...prev];
        if (columnName === 'label') {
          return cuentasCopy.sort(
            (a, b) =>
              a[columnName]?.localeCompare(b[columnName]) *
              (sortOrder === 'asc' ? 1 : -1),
          );
        } else {
          return cuentasCopy.sort(
            (a, b) => (a[columnName] - b[columnName]) * (sortOrder === 'asc' ? 1 : -1),
          );
        }
      });
    }
  }, []);

  const searchEventHandler = useCallback(
    (text: string) => {
      const upperCaseText = text.trim().toUpperCase();
      setSearchInput(text);

      const filteredCuentas = destinos.filter(
        (i) =>
          i.denominacion?.toUpperCase().includes(upperCaseText) ||
          String(i.numeroCuenta).toUpperCase().includes(upperCaseText) ||
          i.nombreCentroCosto?.toUpperCase().includes(upperCaseText) ||
          i.tarjetas.some((tarjeta) =>
            `${tarjeta.denominacion ?? ''} - ${tarjeta.numero ?? ''}`
              .toUpperCase()
              .includes(upperCaseText),
          ),
      );
      setCuentas(filteredCuentas);
    },
    [destinos],
  );

  return (
    <Card>
      {(isLoading || !!saldoDisponible || !!destinos) && (
        <>
          <Card.Body>
            <div className="p-3">
              {!isLoading ? (
                <SearchInput
                  placeholder="Buscar cuenta"
                  helperText="Ingrese un numero de cuenta para realizar la búsqueda"
                  fullWidth
                  value={searchInput}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    searchEventHandler(e?.target?.value ?? '')
                  }
                />
              ) : (
                <InputPlaceholder />
              )}
            </div>
            <SortableTableProvider ref={sortRef} onFilter={filterEventHandler}>
              <Table
                thead={
                  <tr>
                    <SortableTableHeader column="label" disabled={!cuentas}>
                      Centro de costos
                    </SortableTableHeader>
                    <SortableTableHeader column="saldoDisponible" disabled={!cuentas}>
                      Disponible
                    </SortableTableHeader>
                    <th>Monto a transferir</th>
                    <th>Saldo final</th>
                  </tr>
                }
                tbody={
                  <>
                    {(!isLoading &&
                      cuentas?.map((row) => (
                        <FormValue
                          key={'child' + row.numeroCuenta}
                          field={`distribution${row.numeroCuenta}`}
                          render={({ value, errors }) => (
                            <TableRow
                              data-testid={'child' + row.numeroCuenta}
                              $selected={!!value}
                              $error={!!errors && value}
                            >
                              <td className="align-middle">
                                <div>{highlightText(String(row.numeroCuenta ?? ''), searchInput)}</div>
                                <div>{highlightText(String(row.nombreCentroCosto ?? ''), searchInput)}</div>
                                {row.tarjetas.map((tarjeta) => (
                                  <div key={tarjeta.numero}>
                                    {highlightText(
                                      `${tarjeta.denominacion ?? ''} - ${tarjeta.numero ?? ''}`,
                                      searchInput,
                                    )}
                                  </div>
                                ))}
                              </td>
                              <td className="align-middle">
                                {formatCurrency(row.saldoDisponible)}
                              </td>
                              <td className="align-middle">
                                <FormWithValidation.CurrencyInput
                                  data-testid={'distribution-field' + row.numeroCuenta}
                                  name={'distribution' + row.numeroCuenta}
                                  registerOptions={{
                                    max: {
                                      value: saldoDisponible,
                                      message: DISTRIBUTION_ERRORS.amountExceedsMax,
                                    },
                                    min: {
                                      value: 0,
                                      message: DISTRIBUTION_ERRORS.amountBelowMin,
                                    },
                                    setValueAs: (value) => {
                                      return {
                                        numeroCuenta: row.numeroCuenta,
                                        monto: value,
                                      };
                                    },
                                  }}
                                />
                              </td>
                              <td
                                data-testid={'balance-child' + row.numeroCuenta}
                                className="align-middle"
                              >
                                {formatCurrency((row.saldoDisponible ?? 0) + (value ?? 0))}
                              </td>
                            </TableRow>
                          )}
                        />
                      ))) ||
                      [...Array(5)].map(() => (
                        <tr key={nanoid()}>
                          <td key={nanoid()}>
                            <TextPlaceholder height={24} />
                          </td>
                          <td key={nanoid()}>
                            <TextPlaceholder height={24} />
                          </td>
                          <td key={nanoid()}>
                            <InputPlaceholder />
                          </td>
                          <td key={nanoid()}>
                            <TextPlaceholder height={24} />
                          </td>
                        </tr>
                      ))}
                  </>
                }
              />
            </SortableTableProvider>
          </Card.Body>
          <Card.Footer>
            <div className="d-flex justify-content-end">
              {!isLoading ? (
                <>
                  <Button
                    type="button"
                    color="destructive"
                    variant="outline"
                    className="me-2"
                    onClick={clearHandler}
                    disabled={!destinos}
                  >
                    Limpiar
                  </Button>
                  <Button type="submit" disabled={!destinos}>
                    Distribuir
                  </Button>
                </>
              ) : (
                <>
                  <ButtonPlaceholder
                    color="destructive"
                    variant="outline"
                    className="me-2"
                  >
                    Limpiar
                  </ButtonPlaceholder>
                  <ButtonPlaceholder>Distribuir</ButtonPlaceholder>
                </>
              )}
            </div>
          </Card.Footer>
        </>
      )}
    </Card>
  );
};
